import React, { useMemo } from 'react';
import { Button, Section, SectionHeader } from '../../design-system';
import MarketingLayout from '../layouts/MarketingLayout';
import FadeIn from '../components/FadeIn';
import SeoHead from '../seo/SeoHead';
import {
  RESTAURANT_TV_SEO,
  buildFaqJsonLd,
  buildOrganizationJsonLd,
  buildSoftwareJsonLd,
} from '../seo/pages';
import { INSTALLATION_SHOTS } from '../../assets/installationShots';
import screenImage from '../../assets/images/cover3.png';

const USE_CASES = [
  {
    title: 'Digital menu boards',
    prompt: "Today's lunch specials — grilled salmon, pasta primavera, house salad",
    description: 'Update restaurant TV content for menus without Canva, PowerPoint, or a designer.',
  },
  {
    title: 'Happy hour & specials',
    prompt: 'Happy Hour 4–7 PM — $2 off drafts and half-price apps',
    description: 'Push promotions to every restaurant screen the moment you decide.',
  },
  {
    title: 'Hiring & help wanted',
    prompt: 'Now hiring servers and line cooks — apply today',
    description: 'Turn idle restaurant TVs into recruiting screens in seconds.',
  },
  {
    title: 'Events & catering',
    prompt: 'Private dining available this weekend — ask your server',
    description: 'Keep guests informed with clear, on-brand restaurant TV content.',
  },
];

const BOX_FEATURES = [
  {
    title: 'Restaurant TV box that just works',
    description:
      'Plug the INVERT restaurant TV box into any HDMI TV. No expensive digital signage hardware required.',
  },
  {
    title: 'Works with Smart TVs too',
    description:
      'Prefer an app? Install INVERT on Android TV, Fire TV, and supported Smart TVs — same restaurant TV content experience.',
  },
  {
    title: 'Type once, update every screen',
    description:
      'One sentence updates every restaurant display — perfectly formatted for landscape or portrait TVs.',
  },
];

const FAQS = [
  {
    question: 'What is restaurant TV content with INVERT?',
    answer:
      'Restaurant TV content is everything you show on TVs inside your restaurant — menus, daily specials, happy hour, hiring, and promotions. With INVERT, you type what you want and AI creates the on-screen design automatically.',
  },
  {
    question: 'What is the INVERT restaurant TV box?',
    answer:
      'The INVERT restaurant TV box is a small HDMI device you plug into any TV. It connects your screen to INVERT so you can publish restaurant TV content from your phone or computer without buying a full digital signage system.',
  },
  {
    question: 'Do I need a designer to update my restaurant screens?',
    answer:
      'No. Type a sentence like “Happy Hour starts at 4” and INVERT generates typography, layout, and branding for your restaurant TV content automatically.',
  },
  {
    question: 'Can one restaurant TV box update multiple TVs?',
    answer:
      'Each TV needs INVERT installed (via the restaurant TV box or Smart TV app). You still manage all screens from one place — type once and every connected restaurant screen updates.',
  },
  {
    question: 'Is INVERT only for restaurants?',
    answer:
      'Restaurants are a core use case, but INVERT also works for juice bars, cafes, retail, and other venues that need simple TV content without complicated signage software.',
  },
];

export default function RestaurantTv() {
  const jsonLd = useMemo(
    () => [buildOrganizationJsonLd(), buildSoftwareJsonLd(), buildFaqJsonLd(FAQS)],
    []
  );

  return (
    <MarketingLayout>
      <SeoHead
        title={RESTAURANT_TV_SEO.title}
        description={RESTAURANT_TV_SEO.description}
        path={RESTAURANT_TV_SEO.path}
        jsonLd={jsonLd}
      />

      <section className="bg-invert-navy text-white pt-32 lg:pt-40 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-6">
                For Restaurants
              </p>
              <h1 className="font-interblack text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6">
                Restaurant TV content — without the design headache
              </h1>
              <p className="text-xl text-slate-300 mb-4 leading-relaxed">
                Plug in the INVERT <strong className="text-white font-semibold">restaurant TV box</strong>,
                type what you want, and every screen updates instantly.
              </p>
              <p className="text-base text-slate-400 mb-8 leading-relaxed">
                Menus, specials, happy hour, hiring, and promotions — restaurant TV content that looks
                polished, without Canva, PowerPoint, or a digital signage contract.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button to="/screenHostSignup" size="lg">
                  Get Your Restaurant TV Box
                </Button>
                <Button to="/contactUs?intent=host" variant="secondary" size="lg">
                  Talk to Us
                </Button>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <img
                src={screenImage}
                alt="Restaurant TV content displayed on an INVERT screen in a venue"
                className="rounded-2xl shadow-2xl w-full"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      <Section>
        <SectionHeader
          label="Restaurant TV Content"
          title="What restaurants put on their TVs"
          subtitle="Type a sentence. INVERT turns it into on-brand restaurant TV content for every display."
        />
        <div className="grid sm:grid-cols-2 gap-6">
          {USE_CASES.map((item, i) => (
            <FadeIn key={item.title} delay={i * 0.08}>
              <article className="rounded-2xl border border-invert-border bg-white p-6 h-full">
                <h2 className="text-lg font-semibold text-invert-navy mb-2">{item.title}</h2>
                <p className="text-sm text-slate-500 mb-4">{item.description}</p>
                <p className="text-xs uppercase tracking-wider text-invert-orange font-semibold mb-2">
                  Type:
                </p>
                <p className="text-base font-medium text-invert-navy">
                  &ldquo;{item.prompt}&rdquo;
                </p>
              </article>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50" id="restaurant-tv-box">
        <SectionHeader
          label="Restaurant TV Box"
          title="The restaurant TV box that replaces digital signage software"
          subtitle="Connect any TV. Publish restaurant TV content from your phone. No designers required."
        />
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {BOX_FEATURES.map((item, i) => (
            <FadeIn key={item.title} delay={i * 0.1}>
              <div>
                <h3 className="text-lg font-semibold text-invert-navy mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            </FadeIn>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {INSTALLATION_SHOTS.slice(0, 6).map((shot, i) => (
            <FadeIn key={shot.alt} delay={i * 0.06}>
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src={shot.src}
                  alt={shot.alt || 'INVERT restaurant TV box installation'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader
          label="How It Works"
          title="From restaurant TV box to live content in minutes"
          subtitle="Built for busy kitchens and front-of-house teams — not IT departments."
        />
        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 list-none p-0 m-0">
          {[
            {
              step: '01',
              title: 'Plug in the restaurant TV box',
              description: 'Connect INVERT to your existing restaurant TVs via HDMI or Smart TV app.',
            },
            {
              step: '02',
              title: 'Type your restaurant TV content',
              description: 'Menus, specials, hiring — say it in plain English.',
            },
            {
              step: '03',
              title: 'AI designs the screen',
              description: 'Typography, layout, and branding are generated automatically.',
            },
            {
              step: '04',
              title: 'Every TV updates',
              description: 'One prompt publishes across every restaurant screen in your venue.',
            },
          ].map((item, i) => (
            <FadeIn key={item.step} delay={i * 0.08}>
              <li>
                <span className="text-4xl font-bold text-invert-orange/30">{item.step}</span>
                <h3 className="text-lg font-semibold text-invert-navy mt-2 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </li>
            </FadeIn>
          ))}
        </ol>
      </Section>

      <Section className="bg-slate-50" id="faq">
        <SectionHeader
          label="FAQ"
          title="Restaurant TV content & restaurant TV box questions"
          subtitle="Straight answers for restaurant owners and managers."
        />
        <div className="max-w-3xl mx-auto space-y-4">
          {FAQS.map((faq, i) => (
            <FadeIn key={faq.question} delay={i * 0.05}>
              <details className="group rounded-xl border border-invert-border bg-white p-5">
                <summary className="cursor-pointer list-none font-semibold text-invert-navy flex items-center justify-between gap-4">
                  {faq.question}
                  <span className="text-invert-orange text-xl leading-none group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">{faq.answer}</p>
              </details>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section dark className="text-center">
        <FadeIn className="max-w-2xl mx-auto">
          <h2 className="font-interblack text-3xl lg:text-4xl text-white mb-6">
            Ready for better restaurant TV content?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Get the INVERT restaurant TV box, type what you want, and stop wrestling with digital signage.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button to="/screenHostSignup" size="lg">
              Start Free
            </Button>
            <Button to="/for-business" variant="secondary" size="lg">
              See For Business
            </Button>
          </div>
        </FadeIn>
      </Section>
    </MarketingLayout>
  );
}
